import { useCallback, useEffect, useState } from "react";
import { env } from "../env";
import { createCleanFrontendUrl, hardReloadFrontend } from "./frontendReload";

const DEFAULT_CHECK_INTERVAL_MS = 2 * 60 * 1000;

export interface UseFrontendUpdateOptions {
  currentVersion?: string;
  checkIntervalMs?: number;
  versionUrl?: string;
  reloadOnPreloadError?: boolean;
}

interface FrontendUpdateState {
  updateAvailable: boolean;
  availableVersion: string | null;
  checkForUpdate: () => Promise<void>;
  reload: () => void;
}

interface VersionResponse {
  version?: unknown;
}

export function useFrontendUpdate({
  currentVersion = env.VITE_RELEASE_SHA,
  checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS,
  versionUrl = "/version.json",
  reloadOnPreloadError = true,
}: UseFrontendUpdateOptions = {}): FrontendUpdateState {
  const [availableVersion, setAvailableVersion] = useState<string | null>(null);
  const normalizedCurrentVersion = currentVersion?.trim();

  const checkForUpdate = useCallback(async () => {
    if (!normalizedCurrentVersion || typeof window === "undefined") return;

    try {
      const url = new URL(versionUrl, window.location.origin);
      url.searchParams.set("_", Date.now().toString());
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) return;

      const payload = (await response.json()) as VersionResponse;
      const deployedVersion = typeof payload.version === "string" ? payload.version.trim() : "";
      setAvailableVersion(deployedVersion && deployedVersion !== normalizedCurrentVersion ? deployedVersion : null);
    } catch {
      // Version checks are best effort and must never interrupt the application.
    }
  }, [normalizedCurrentVersion, versionUrl]);

  const reload = useCallback(() => {
    hardReloadFrontend();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const cleanUrl = createCleanFrontendUrl(window.location.href);
    if (cleanUrl) window.history.replaceState(window.history.state, "", cleanUrl);
  }, []);

  useEffect(() => {
    if (!normalizedCurrentVersion || typeof window === "undefined") return;

    void checkForUpdate();
    const intervalId = window.setInterval(() => void checkForUpdate(), checkIntervalMs);
    const handleFocus = () => void checkForUpdate();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void checkForUpdate();
    };
    const handlePreloadError = (event: Event) => {
      event.preventDefault();
      const reloadKey = `canucci:preload-reload:${normalizedCurrentVersion}`;
      try {
        if (window.sessionStorage.getItem(reloadKey)) return;
        window.sessionStorage.setItem(reloadKey, "1");
      } catch {
        // Proceed with recovery even when session storage is unavailable.
      }
      hardReloadFrontend();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (reloadOnPreloadError) window.addEventListener("vite:preloadError", handlePreloadError);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (reloadOnPreloadError) window.removeEventListener("vite:preloadError", handlePreloadError);
    };
  }, [checkForUpdate, checkIntervalMs, normalizedCurrentVersion, reloadOnPreloadError]);

  return {
    updateAvailable: availableVersion !== null,
    availableVersion,
    checkForUpdate,
    reload,
  };
}
